import { useMemo, useState } from 'react';
import { SortDirection } from '../enum/SortDirection';

/**
 * Custom React hook for sorting tabular data.
 *
 * @template T Type of objects in the table (must extend Record<string, any>)
 *
 * @param {T[]} data - Input array of data to be sorted
 *
 * Returned object containing:
 * sortedData - Memoized sorted data array
 * {{ key: string; direction: 'asc' | 'desc' } | null} sortConfig - Current sorting configuration
 * {(key: string) => void} toggleSort - Function to toggle sorting by column key
 *
 *
 * - Enables sorting by any object key
 * - First click on a column sets sorting to 'asc'
 * - Subsequent clicks on the same column toggle direction ('asc' <-> 'desc')
 * - Clicking a different column resets sorting direction to 'asc'
 * - Uses useMemo for performance optimization to avoid unnecessary re-sorting
 *
 */

export const useTableSort = <T extends Record<string, any>>(data: T[]) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  } | null>(null);

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aVal = typeof a[sortConfig.key] === 'string' ? a[sortConfig.key].trim() : a[sortConfig.key];

      const bVal = typeof b[sortConfig.key] === 'string' ? b[sortConfig.key].trim() : b[sortConfig.key];

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortConfig.direction === SortDirection.ASCENDING
          ? aVal.localeCompare(bVal, 'sr', { sensitivity: 'base' })
          : bVal.localeCompare(aVal, 'sr', { sensitivity: 'base' });
      }

      const aNum = aVal ?? 0;
      const bNum = bVal ?? 0;

      if (aNum < bNum) return sortConfig.direction === SortDirection.ASCENDING ? -1 : 1;
      if (aNum > bNum) return sortConfig.direction === SortDirection.ASCENDING ? 1 : -1;

      return 0;
    });
  }, [data, sortConfig]);

  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) {
        return { key, direction: SortDirection.ASCENDING };
      }

      return {
        key,
        direction: prev.direction === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING,
      };
    });
  };

  return {
    sortedData,
    sortConfig,
    toggleSort,
  };
};
